/* eslint-disable no-mixed-operators */
import { faArrowUpWideShort, faArrowDownWideShort, faRightLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from "@fortawesome/free-regular-svg-icons";
import { message, Button, Modal, Col, Badge, notification, List, Row, Space, Typography } from 'antd';
import { WarningFilled } from '@ant-design/icons'
import dayjs from 'dayjs'
import constants from '../Constants/constants';
import { cookiesToDelete, fetchApi, fetchStaffApi } from '../Services/Api/index'
import DOMPurify from 'dompurify';
import { encodeURI } from "js-base64";
import { COOKIE, createCookie, getCookie } from '../Services/Cookie';

const { Paragraph } = Typography;

// Obvious words that should never appear inside a password.
const OBVIOUS_PASSWORD_WORDS = ['password', 'qwerty', 'admin', 'welcome', 'letmein', 'login', 'aol', 'test'];
// Character runs used to detect keyboard/alphanumeric sequences (forward and reverse).
const PASSWORD_SEQUENCES = ['abcdefghijklmnopqrstuvwxyz', '0123456789', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

// Returns true if the password contains an ascending or descending run of `runLength` characters
// taken from any known sequence (e.g. "1234", "dcba", "qwer").
const hasSequentialChars = (password, runLength = 4) => {
	const lower = password.toLowerCase();
	for (let i = 0; i <= lower.length - runLength; i++) {
		const chunk = lower.substring(i, i + runLength);
		const matched = PASSWORD_SEQUENCES.some((seq) => {
			const reversed = seq.split('').reverse().join('');
			return seq.includes(chunk) || reversed.includes(chunk);
		});
		if (matched) {
			return true;
		}
	}
	return false;
};

// Returns the obvious word contained in a password (if any), so callers can
// surface the exact word the user typed instead of a generic example.
export const findObviousPasswordWord = (password) => {
	const lowerPassword = password ? password.trim().toLowerCase() : '';
	return OBVIOUS_PASSWORD_WORDS.find((word) => lowerPassword.includes(word)) || null;
};

//function to validate a new password against the app's password policy:
//min 14 chars, upper, lower, digit, special char (spaces allowed within, edges trimmed),
//no obvious words, no repeated characters and no sequences.
export const validatePasswordRules = (password) => {
	// Leading/trailing spaces are not part of the password, so validate the trimmed value.
	const trimmedPassword = password ? password.trim() : password;
	if (!trimmedPassword) {
		return constants.PasswordRegistration;
	}
	const passwordValidate = /^(?=.{14,}$)(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).+$/;
	if (!passwordValidate.test(trimmedPassword)) {
		return constants.PasswordValidation;
	}
	const obviousWord = findObviousPasswordWord(trimmedPassword);
	if (obviousWord) {
		// Surface the exact word the user typed so it's obvious what needs to change.
		return constants.PasswordObvious.replace('"password"', `"${obviousWord}"`);
	}
	if (/(.)\1\1/.test(trimmedPassword)) {
		return constants.PasswordRepeated;
	}
	if (hasSequentialChars(trimmedPassword)) {
		return constants.PasswordSequence;
	}
	return null;
};
//fuction for current start date and end date.
export const getCurrentDayStartAndEnd = () => {
	// Get the current date and time
	const currentDate = dayjs();
	// Get the start of the day (00:00:00)
	const startOfDay = currentDate.startOf('day');
	// Get the end of the day (23:59:59)
	const endOfDay = currentDate.endOf('day');
	return {
		start: startOfDay.format('MMM D, YYYY h:mm A'),  // Format the start time
		end: endOfDay.format('MMM D, YYYY h:mm A')       // Format the end time
	};
};
//fuction to performSearch.
export const performSearch = (search, originalData, fieldNames, isArray) => {
	if (!search) {
		return originalData;
	}
	// Normalize the search string by replacing multiple spaces with a single space
	const normalizedSearch = search?.toLowerCase()?.replace(/\s+/g, ' ')?.trim();

	return originalData?.filter((dataItem) =>
		fieldNames.some((fieldName) => {
			// Normalize the data item's field value by replacing multiple spaces with a single space
			const fieldValue = (!Array.isArray(dataItem?.[fieldName])) ? dataItem?.[fieldName]?.toLowerCase().replace(/\s+/g, ' ').trim() : "";
			return fieldValue?.includes(normalizedSearch) || Array.isArray(dataItem?.[fieldName]) && searchNestedData(dataItem?.[fieldName], search);
		})
	);
}
// Function to recursively search within nested data
const searchNestedData = (data, text) => {
	if (!Array.isArray(data)) return false;

	for (let i = 0; i < data.length; i++) {
		const item = data[i];
		if (typeof item === 'object') {
			const values = Object.values(item);
			for (let j = 0; j < values.length; j++) {
				const value = values[j];
				if (typeof value === 'string' && value.toLowerCase().includes(text.toLowerCase())) {
					return true;
				}
				if (Array.isArray(value) && searchNestedData(value, text)) {
					return true;
				}
			}
		}
	}
	return false;
}
export const tableHeaderTitle = (title) => {
	return <span className='fw-bold'>{title}</span>
}
export const getModal = (title, content, modalVisible, closeModal, setterFunc, setterKeysFunc) => {
	return (
		<Modal
			open={modalVisible}
			onCancel={() => { closeModal(setterFunc); setterKeysFunc() }}
			title={title}
			className="!top-5"
			footer={null}
			width={815}
			centered
		>
			{content}
		</Modal>
	)
}
//DisplayModal onclick
export const showModal = (setShowModalFunc) => {
	setShowModalFunc(true);
}
//CloseModal onclick
export const closeModal = (setShowModalFunc) => {
	setShowModalFunc(false);
};
export const getGpaLevelsAndModifyDataFormat = (setterFunc, setLoading) => {
	setLoading(true)
	fetchStaffApi(`/get-gpa-levels`, 'GET').then((res) => {
		setLoading(false)
		if (res?.data) {
			const groupedData = {};
			// Group data by 'year_in_program'
			res?.data.forEach(item => {
				const key = item.year_in_program;
				if (!groupedData[key]) {
					groupedData[key] = {
						key,
						year_in_program: `${item.year_in_program}`,
						fall: 'Fall',
						spring: 'Spring',
						summer: 'Summer',
						fallGpa: null,
						springGpa: null,
						summerGpa: null,
					};
				}
				// Populate GPA values based on 'semester_type'
				switch (item.semester_type) {
					case '1':
						groupedData[key].fallGpa = item.gpa;
						break;
					case '2':
						groupedData[key].springGpa = item.gpa;
						break;
					case '3':
						groupedData[key].summerGpa = item.gpa;
						break;
					default:
						break;
				}
			});
			const formattedData = Object.values(groupedData);
			setterFunc(formattedData);
		} else {
			setterFunc([]);
		}
	}).catch(() => {
		setLoading(false)
		message.error(constants.ErrorMessage)
	});
}

//function to get status name using id
export const getStatusName = (status_id) => {
	const mapStatus = {
		"1": constants.Dismissed,
		"2": constants.Probation,
		"3": constants.LeftProgram,
		"4": constants.Break,
		"5": constants.InActive,
		"6": constants.SlpMajor,
		"7": constants.Active,
		"8": constants.OffProbation,
		"9": constants.Undeclared,
	};
	return mapStatus[status_id];
};

export const getYearInPrgAbbr = (year) => {
	switch (year) {
		case 'Freshman/First-Year':
			return 'Fr';
		case 'Sophomore':
			return 'So';
		case 'Junior':
			return 'Jr';
		case 'Senior/1st-Year Grad':
			return 'G1';
		case 'Senior/1st-Year Grad Year':
			return 'G1';
		case '2nd-Year Grad':
			return 'G2';
		case 'Part Time':
			return 'PT';
		default:
			return ''
	}
}

export const getTypeAbbr = (type) => {
	switch (type) {
		case 'Full Time':
			return 'FT';
		case 'Part Time':
			return 'PT';
		case 'Undeclared':
			return 'UD';
		case 'Prerequisites Only':
			return 'PR';
		default:
			return ''
	}
}

export const HighlightedText = ({ text, highlight }) => {
	const escapeRegExp = (string) => {
		return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}

	if (!highlight?.trim()) {
		return <span>{text}</span>;
	}

	const escapedHighlight = escapeRegExp(highlight);
	const parts = text?.split(new RegExp(`(${escapedHighlight})`, 'gi'));

	return (
		<span>
			{parts?.map((part, i) => (
				<span key={i} className={part?.toLowerCase() === highlight?.toLowerCase() ?
					'highlight' : ''}>
					{part}
				</span>
			))}
		</span>
	);
};

export const HighlightedHTML = ({ html, highlight }) => {
	if (!highlight?.trim()) {
		return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}></div>;
	}

	const sanitizedHTML = DOMPurify.sanitize(html);

	// Create a temporary container to hold the HTML content
	const container = document.createElement('div');
	container.innerHTML = sanitizedHTML;

	// Recursive function to traverse and highlight text nodes
	const highlightTextNodes = (node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			const regex = new RegExp(`(${highlight})`, 'gi');
			const parts = node.textContent.split(regex);

			if (parts.length > 1) {
				const fragment = document.createDocumentFragment();
				parts.forEach((part) => {
					if (regex.test(part)) {
						const span = document.createElement('span');
						span.className = 'highlight';
						span.textContent = part;
						fragment.appendChild(span);
					} else {
						fragment.appendChild(document.createTextNode(part));
					}
				});
				node.replaceWith(fragment);
			}
		} else {
			Array.from(node.childNodes).forEach(highlightTextNodes);
		}
	};

	// Apply highlighting to all child nodes
	Array.from(container.childNodes).forEach(highlightTextNodes);

	return <div dangerouslySetInnerHTML={{ __html: container.innerHTML }}></div>;
};

//function related on keypress input || Allow only numbers (0-9)
export const handleKeyPressHandler = (event, maxLength) => {
	const charCode = event.which || event.keyCode;
	// Allow only numbers (0-9)
	if (charCode < 48 || charCode > 57) {
		event.preventDefault();
	}
	// Limit the input length to the specified maximum
	const currentValue = event.target.value;
	if (!maxLength) return
	if (currentValue.length >= maxLength) {
		event.preventDefault();
	}
};

//fuction to allow only alphabets.
export const allowAlphabets = (event) => {
	// Allow only alphabets, hyphen, underscore, space, or single quote
	const regex = /^[A-Za-z\-_' ]$/i;
	if (!regex.test(event.key)) {
		event.preventDefault();
	}
};

// Custom validation function for the rule
export const generateNumberValidator = (maxLength, message) => (_, value) => {
	if (!value) {
		return Promise.resolve();
	}
	if (value.length >= maxLength) {
		return Promise.resolve();
	} else {
		return Promise.reject(new Error(message));
	}
};

//Tiny MCE text editor validator
export const handleValidateTextEditor = (rule, value, callback, field, form) => {
	try {
		if (!form.getFieldValue(field)) {
			throw new Error(constants.FieldRequired);
		}
		callback();
	} catch (err) {
		callback(err);
	}
};

//Formating phone number to add hyphens
export const phoneNumberFormatter = (value) => {
	// Remove non-numeric characters
	const numericValue = value?.replace(/\D/g, '');
	// Add hyphens after every 3 and 6 numbers
	// preg_replace("/^(\d{3})(\d{3})(\d{4})$/", "$1-$2-$3"
	const formattedValue = numericValue?.replace(/(\d{3})(\d{3})?(\d{0,4})?/, (_, first, second, third) => {
		let result = `${first}`;
		if (second) result += `-${second}`;
		if (third) result += `-${third}`;
		return result;
	});
	return formattedValue;
};

export const beforeProfileUpload = (file) => {
	const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
	if (!isJpgOrPng) {
		message.error(constants.InvalidFileFormat);
	}
	const isLt2M = file.size / 1024 / 1024 < 2;
	if (!isLt2M) {
		message.error('Maximum file size accepted is 2MB.');
	}
	return isJpgOrPng && isLt2M;
};

export const getBase64 = (img, callback) => {
	const reader = new FileReader();
	reader.addEventListener('load', () => callback(reader.result));
	return reader.readAsDataURL(img);
};

export const beforeUploadCheckFileType = (file, showMessage = true) => {
	const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
	if (!allowedTypes.includes(file.type)) {
		if (showMessage) {
			message.error(constants.InvalidFileFormat);
		}
		return false;
	}
	const isLt2M = file.size / 1024 / 1024 < 2;
	if (!isLt2M) {
		if (showMessage) {
			message.error(constants.MaxFileSizeMessage);
		}
		return false;
	}
	return true;
};

export const CustomSortIcon = ({ sortOrder }) => {
	if (sortOrder === 'ascend') {
		return <FontAwesomeIcon icon={faArrowUpWideShort} />;
	} else if (sortOrder === 'descend') {
		return <FontAwesomeIcon icon={faArrowDownWideShort} />;
	}
	return <FontAwesomeIcon icon={faRightLeft} rotation={90} />;
};

export const getNextAndPreviousButtons = (current, type, originalElement) => {
	if (type === 'prev') {
		return <Button className='prevBtn' size='small'>{constants.Previous}</Button>;
	} else if (type === 'next') {
		return <Button className='nextBtn' size='small'>{constants.Next}</Button>;
	}
	return originalElement;
};
export const showTotalItems = (total, range, arr) => {
	return <span className="fw-medium text-sm">{`Showing ${range[0]}-${range[1]} of ${arr.length} entries`}</span>
};

export function myTrim(myString) {
	if (!myString) return '';
	return myString.replace(/^\s+|\s+$/gm, '');
}
export function removeExtraSpaces(myString) {
	if (!myString) return '';
	let tempStr = myTrim(myString);
	return tempStr.replace(/(^\s+|\s+$)/g, '').replace(/&nbsp;/g, ' ').trim();
}

export function handleTableChange(current, filters, sorter, currentDataSource, fromTableChange) {
	const defaultSort = (data = []) => {
		if (!sorter?.field || !sorter?.order) {
			return data;
		}

		return [...data].sort((a, b) => {
			const valueA = a?.[sorter.field] ?? '';
			const valueB = b?.[sorter.field] ?? '';

			// String sorting
			if (typeof valueA === 'string' || typeof valueB === 'string') {
				const comparison = valueA?.toString()?.localeCompare(valueB?.toString());
				return sorter.order === 'ascend' ? comparison : -comparison;
			}

			// Number sorting
			const comparison = valueA - valueB;
			return sorter.order === 'ascend' ? comparison : -comparison;
		});
	};

	return {
		current,
		sorter,
		data: fromTableChange
			? currentDataSource
			: defaultSort(currentDataSource || [])
	};
}

export function handleRowSelect(newSelectedRowKeys = [], selectedRowData = [], record = null, index = null, getters, setters, select = 'single', selectAll = false, rowKey = 'id') {
	let selRowKeys = newSelectedRowKeys
	let selRowData = selectedRowData

	if (selectAll) {
		setters?.setRowKeys(selRowKeys);
		setters?.setRowData(selRowData);
		return
	}
	if (index !== null && index > -1) {
		let currentKeys = [...getters?.rowKeys]
		let currentData = [...getters?.rowData]

		let keyIndex = currentData?.findIndex((it) => it?.[rowKey] === record?.[rowKey])
		if (keyIndex > -1) {
			currentKeys?.splice(keyIndex, 1)
			currentData?.splice(keyIndex, 1)
		} else {
			if (select === 'multi') {
				currentKeys.push(record?.[rowKey])
				currentData.push(record)
			} else {
				currentKeys?.splice(0, 1, record?.[rowKey])
				currentData?.splice(0, 1, record)
			}
		}
		selRowKeys = currentKeys
		selRowData = currentData
	}
	setters?.setRowKeys(selRowKeys);
	setters?.setRowData(selRowData);
}
//function to filter select option.
export const filterSelectOption = (input, option) => {
	return option?.children?.toLowerCase()?.includes(input?.toLowerCase());
};

export function sortTwoNumbers(a, b, key, isDate = false) {
	let valueA = a[key];
	let valueB = b[key];
	if (isDate) {
		valueA = valueA !== null && valueA !== undefined ? new Date(valueA) : -Infinity;
		valueB = valueB !== null && valueB !== undefined ? new Date(valueB) : -Infinity;
	} else {
		valueA = valueA !== null && valueA !== undefined ? Number.parseInt(valueA) : -Infinity;
		valueB = valueB !== null && valueB !== undefined ? Number.parseInt(valueB) : -Infinity;
	}

	if (isNaN(valueA) && isNaN(valueB)) {
		return 0;
	}
	if (isNaN(valueA)) {
		return 1;
	}
	if (isNaN(valueB)) {
		return -1;
	}
	return valueA - valueB;
}
export const compareArrays = (arr1, arr2) => {
	if (arr1.length !== arr2.length) {
		return false;
	}
	for (let i = 0; i < arr1.length; i++) {
		if (arr1[i] !== arr2[i]) {
			return false;
		}
	}
	return true;
};

export const disableSpaceBar = (event) => {
	if (event.key === ' ') {
		event.preventDefault();
	}
};

export const checkEmptySpace = (event, formItem) => {
	if (!formItem) disableSpaceBar(event);
}

export const checkWhitespaces = (e) => {
	if (e.target.value?.charAt(0) === ' ' || e.target.value?.charAt(0) === '\n') return true
}

export const surveyRedirectRoute = (record = null, student = true, surveyIdentifier = 'survey_id', login = '1') => {
	let survey_id = record?.[surveyIdentifier] || ''
	let survey_type = record?.survey_type || ''
	let semester_id = record?.semester_id || null
	let role = student ? 's' : 'f'
	let loggedIn = login === '1'
	let encoded = encodeURI(`survey_id=${survey_id}&survey_type=${survey_type}&role=${role}&login=${loggedIn}&semester_id=${semester_id}`)

	if (student)
		return `/answer-survey-student?${encoded}`
	return `/answer-survey?${encoded}`
}

//generate columns to display advisees
export const generateAdviseeMenteeListColumn = (list, field, setter) => {
	if (list?.length === 0) {
		setter([])
		return
	}
	let numColumns = 4
	let nameList = list?.map((item) => item?.[field])
	const studentsPerColumn = Math.ceil(nameList.length / numColumns);
	const columns = [];
	for (let i = 0; i < numColumns; i++) {
		columns.push(
			<Col span={24 / numColumns} key={i}>
				<ul>
					{
						nameList
							.slice(i * studentsPerColumn, (i + 1) * studentsPerColumn)
							.map((student, index) => (
								<li><b>{student}</b></li>
							))
					}
				</ul>
			</Col>
		);
	}
	setter(columns)
}

export const getStudentDetailsById = async (id) => {
	try {
		const result = await fetchStaffApi(`/get-student-data/${id}`, 'post' , {})
		if (result?.code === '200') return result?.data?.student_data?.[0]
		message.error(constants.ErrorMessage);
		return null
	} catch (err) {
		message.error(constants.ErrorMessage);
	}
}

export const checkisAdvisorAndReportingTo = (isAdvisingFaculty, isOnline) => {
	// let reportingTo = false
	const role = getCookie(COOKIE.Role)
	// const isAdminOrClinicsDirector = role === '1' || role === '3' || role === '6'
	// const isReportingTo = getCookie(COOKIE.ReportingTo) || false;
	// const isReportingToOnline = isReportingTo ? getCookie(COOKIE.ReportingTo) === '2' : false
	// if(isAdminOrClinicsDirector) return true
	// if (!isReportingTo && !isAdvisingFaculty) return false //HIDE IF NOT REPORTING TO AND NOT ADVISOR
	// if (!isReportingTo && isAdvisingFaculty) return false //HIDE IF NOT REPORTING TO BUT IS AN ADVISOR
	// if (isReportingTo && !isAdvisingFaculty) return false //HIDE IF A REPORTING TO BUT NOT ADVISOR
	// reportingTo = ((isReportingToOnline && isOnline) //SHOW IF REPORTING FOR ONLINE AND CURRENT STUDENT IS ONLINE
	// 	|| (!isReportingToOnline && !isOnline)) //SHOW IF REPORTING FOR IN-PERSON AND CURRENT STUDENT IS IN-PERSON
	// if (reportingTo) return true
	if (role !== '4') return true //HIDE BUTTON FOR ADJUNCT FACULTY
	return false
}

export const isAdvisorAndReportingTo = (faculty_id, online) => {
	const userId = getCookie(COOKIE.UserId)
	const isAdvisingFaculty = getCookie(COOKIE.IsAdvisor) === "1" && (userId === faculty_id?.toString())
	const isOnline = online?.toString() === '1'
	return checkisAdvisorAndReportingTo(isAdvisingFaculty, isOnline)
}

export const notificationHandler = async (notif, navigateFn, props) => {
	const userRole = getCookie(COOKIE.Role)
	const isStudent = userRole === '0'

	const getBreadcrumbRoutes = () => {
		if (props?.isDashboard) return
		if (props?.fromPage)
			return [
				...props?.breadcrumbItems,
				{ title: props?.fromPage, path: props?.fromRoute }
			]
		if (isStudent)
			return [{ title: constants.Notifications, path: '/student-notifications' }];
		if (userRole === '1' || userRole === '3' || userRole === '6')
			return [{ title: constants.NotificationList, path: '/notification-list' }];
		else
			return [{ title: constants.Notifications, path: '/notifications' }];
	}

	const createEventDispatch = () => {
		// CREATE EVENT FOR STUDENT INFO TABS TO LISTEN AND UPDATE UI
		window.dispatchEvent(new Event('tab-change-event'));
	}

	const getCurrentRoute = () => {
		//GENERATE CURRENT URL ROUTE
		let currentRouteSplit = window.location.href?.split('/')
		let currentRoute = currentRouteSplit[currentRouteSplit?.length - 1]
		return `/${currentRoute}`
	}

	let notif_text = notif?.notification_text?.toLowerCase()
	if (notif?.notification_url?.includes("student-mentor-request")) {
		props?.setNotificationId(notif?.notification_id)
		props?.setMentorRequestModalOpen(true)
	} else if (notif?.notification_url?.includes('survey-result')) {
		let surveyName = notif_text?.split('result')?.[0]?.trim() || ''
		let splitUrl = notif?.notification_url?.split('/')
		let currentRoute = getCurrentRoute()
		const locationData = {
			from: constants.NotificationList,
			from_route: "/notifications",
		}
		let urlProps = {
			selectedRecord: {
				course_id: splitUrl?.[1],
				id: splitUrl?.[2],
				survey_name: surveyName,
				survey_type: notif?.survey_type,
				delete_check: Number.parseInt(notif?.surveys_delete_check),
				notif_id: notif?.id,
				faculty_id: notif?.faculty_id,
				section: splitUrl?.[3],
			},
			previousRoute: currentRoute,
			locationData,
		}
		setContextOnLoad('selectedMenuItem', props?.setSelectedMenuItem, 'learning-outcomes')
		setContextOnLoad('selectedSubMenuItem', props?.setSelectedSubMenuItem, 'surveys')
		if (getBreadcrumbRoutes()) breadcrumbHelper(getBreadcrumbRoutes(), props?.setBreadcrumbItems);
		navigateFn(`/${splitUrl?.[0]}`, { state: urlProps })
	} else if (notif_text?.includes('survey')) {
		if (getBreadcrumbRoutes()) breadcrumbHelper(getBreadcrumbRoutes(), props?.setBreadcrumbItems);
		let splitUrl = notif?.notification_url?.split('/')
		notif = { ...notif, semester_id: splitUrl?.[1] }
		navigateFn(surveyRedirectRoute(notif, props?.isStudent, 'id_for_notification'));
		setContextOnLoad('selectedMenuItem', props?.setSelectedMenuItem, props?.isStudent ? 'all-surveys' : 'my-surveys')
		setContextOnLoad('selectedSubMenuItem', props?.setSelectedSubMenuItem, 'surveys')
	} else if (notif_text?.includes('advisory')) {
		createCookie("facultyId", notif?.faculty_id, null, "/");
		sessionStorage.setItem('activeStudentTab', isStudent ? 'advisory-notes' : 'advising-notes')
		sessionStorage.setItem('activeStudentTabTitle', constants.AdvisingNotes)
		sessionStorage.setItem('activeExtraButton', false)
		sessionStorage.removeItem('activeStudentSubTab')
		if (getBreadcrumbRoutes()) breadcrumbHelper(getBreadcrumbRoutes(), props?.setBreadcrumbItems);
		createEventDispatch()
		if (props?.isStudent) {
			navigateFn(`/${notif?.notification_url}`, {
				state: {
					tabkey: "advisory-notes",
					tab_title: constants.AdvisoryNotes,
					from: constants.Notifications,
					from_route: "/student-notifications",
				}
			})
			return
		}

		let splitUrl = notif?.notification_url?.split('/')
		const res = await getStudentDetailsById(splitUrl?.[1])
		if (!res) return
		//GENERATE REDIRECT URL
		let currentRoute = getCurrentRoute()
		let urlProps = {
			student_id: splitUrl?.[1],
			tabkey: "advising-notes",
			tab_title: constants.AdvisingNotes,
			from: constants.Notifications,
			from_route: currentRoute,
			student_name: splitUrl?.[2],
			sem_id: res?.semester_details_id || splitUrl?.[3],
			year_in_program: splitUrl?.[4],
			reloadTab: props?.reloadTab,
			online: res?.online,
			state_name: res?.student_permanent_state_text,
			student_current_country: res?.student_current_country,
			faculty_id: res?.faculty_id,
		}
		setContextOnLoad('selectedMenuItem', props?.setSelectedMenuItem, 'advising-documents')
		setContextOnLoad('selectedSubMenuItem', props?.setSelectedSubMenuItem, 'advising')
		navigateFn(`/${splitUrl?.[0]}`, { state: urlProps });
	} else if (notif_text?.includes('result')) {
		let surveyName = notif_text?.split('result')?.[0]?.trim() || ''
		let splitUrl = notif?.notification_url?.split('/')
		let currentRoute = getCurrentRoute()
		const locationData = {
			from: constants.NotificationList,
			from_route: "/notifications",
		}
		let urlProps = {
			selectedRecord: {
				course_id: splitUrl?.[1],
				id: splitUrl?.[2],
				survey_name: surveyName,
				survey_type: notif?.survey_type,
				delete_check: Number.parseInt(notif?.surveys_delete_check),
				notif_id: notif?.id,
				faculty_id: notif?.faculty_id,
				section_no: splitUrl?.[3],
			},
			previousRoute: currentRoute,
			locationData,
		}
		setContextOnLoad('selectedMenuItem', props?.setSelectedMenuItem, 'manage-surveys')
		setContextOnLoad('selectedSubMenuItem', props?.setSelectedSubMenuItem, 'surveys')
		if (getBreadcrumbRoutes()) breadcrumbHelper(getBreadcrumbRoutes(), props?.setBreadcrumbItems);
		navigateFn(`/${splitUrl?.[0]}`, { state: urlProps })
	} else if (notif?.notification_url?.includes('student-information') || notif?.notification_url?.includes('clinical-student-clearances')) {
		const rolesAllowed = ['1', '3', '6', '2', '5']
		if (!isStudent /* && !reportingTo */ && !rolesAllowed.includes(userRole)) {
			message.error(constants.ClinicsNotiErrMsg)
			return;
		}
		let splitUrl = notif?.notification_url?.split('/')
		let studentId = props?.isStudent ? notif?.student_id : splitUrl?.[1]
		let areaId = props?.isStudent ? splitUrl?.[1] : splitUrl?.[2]
		let clinicalTabKey = areaId === '7' ? 'observations' : 'clearance-tabs'
		let clinicalTabTitle = areaId === '7' ? constants.Observations : constants.Clearances
		let tab = constants.ClinicalAreasTabKeys.find((it) => it.value === areaId)
		if (areaId <= '4') {
			sessionStorage.setItem('activeStudentTab', 'clearance-tabs')
			sessionStorage.setItem('activeStudentTabTitle', constants.Clearances)
			sessionStorage.setItem('activeStudentSubTab', tab?.key) //SET CLINICAL TAB ONLY IF IT IS CLEARANCE PAGES
		} else if (areaId === '7') {
			sessionStorage.setItem('activeStudentTab', 'observations')
			sessionStorage.setItem('activeStudentTabTitle', constants.Observations)
		}
		if (getBreadcrumbRoutes()) breadcrumbHelper(getBreadcrumbRoutes(), props?.setBreadcrumbItems);
		createEventDispatch()
		//REDIRECT LOGIC FOR STUDENT LOGIN
		if (props?.isStudent) {
			setContextOnLoad('selectedMenuItem', props?.setSelectedMenuItem, 'clinical-student-clearances')
			navigateFn("/clinical-student-clearances")
			return
		}
		//REDIRECT LOGIC FOR NON-STUDENT LOGIN
		const res = await getStudentDetailsById(studentId)
		if (!res) return

		sessionStorage.setItem('activeExtraButton', isAdvisorAndReportingTo(res?.faculty_id, res?.online))
		// createEventDispatch()
		navigateFn("/student-information", {
			state: {
				student_id: res?.id,
				student_type: res?.student_type,
				// student_semester: showTab ? tabKey : "0",
				from: props?.fromPage || constants.Notifications,
				from_route: props?.fromRoute || "/notifications",
				tabkey: clinicalTabKey,
				tab_title: clinicalTabTitle,
				student_name: res?.student_first_name + " " + res?.student_last_name,
				isArchived: res?.is_active === '3',
				year_in_program: res?.year_in_program,
				sem_id: res?.semester_details_id,
				is_not_logged_in: res?.is_active === '0',
				online: res?.online,
				state_name: res?.student_permanent_state_text,
				student_current_country: res?.student_current_country,
				reloadTab: props?.reloadTab,
				faculty_id: res?.faculty_id,
			}
		});
	} else if (notif?.notification_url?.includes('clinical-verification-letter')) {
		navigateFn('/clinical-verification-letter',
			{
				state: {
					student_id: getCookie(COOKIE.UserId),
					from: "/notifications",
					fromText: constants.Notifications,
					is_notification: true,
					notif_id: notif?.id
				}
			})
	} else if (notif?.notification_text === "Clinics Note Added") {
		if (getBreadcrumbRoutes()) breadcrumbHelper(getBreadcrumbRoutes(), props?.setBreadcrumbItems);
		sessionStorage.setItem('activeStudentTab', 'clinical-notes')
		sessionStorage.setItem('activeStudentTabTitle', constants.ClinicalNotes)
		// sessionStorage.setItem('activeExtraButton', checkIsAdvisorOrAdmin())
		createEventDispatch()
		navigateFn('/student-notes',
			{
				state: {
					tabkey: "clinical-notes",
					tab_title: constants.ClinicalNotes,
					from: constants.Notifications,
					from_route: "/student-notifications",
				}
			})
	} else if (notif?.notification_url?.includes('clinical-observations-hours')) {
		setContextOnLoad('selectedMenuItem', props?.setSelectedMenuItem, 'clinical-observation-requirements')
		navigateFn('/clinical-observations-hours');
	} else {
		if (getBreadcrumbRoutes()) breadcrumbHelper(getBreadcrumbRoutes(), props?.setBreadcrumbItems);
		let url = `/${notif?.notification_url}`
		navigateFn(url);
	}
}

export const badgeNotification = (count) => {
	if (count?.length === 0) {
		return (
			<Button type="text" className="menu-btn">
				<FontAwesomeIcon icon={faBell} className="text-white text-lg" />
			</Button>
		)
	} else {
		return (
			<Button type="text" className="menu-btn">
				<Badge
					count={count?.length}
					size="small"
					showZero={true}
					className="badge-style"
				>
					<FontAwesomeIcon icon={faBell} className="text-white text-lg" />
				</Badge>
			</Button>
		)
	}
}

export const NotificationList = ({ item, onClickHandler }) => {
	const warningIcon = (notif) => {
		if (notif?.priority === "1") return <WarningFilled className="error-icon" />
		else if (notif?.priority === "2") return <WarningFilled className="input-info" />
		else return <WarningFilled className="partial-error" />
	}

	const verificationLetterText = 'Successful verification of observation documents'

	const isClinicalNoteFlag = item?.notification_text === 'Clinics Note Added';

	const isVerificationLetterFlag =
		item?.notification_text_details?.toLowerCase() === verificationLetterText.toLowerCase();

	const isClickable = item?.notification_url && !isVerificationLetterFlag || isClinicalNoteFlag;

	const handleClick = () => {
		if (isClickable) onClickHandler(item);
	};

	return (
		<div
			className={isClickable ? "cursor-pointer" : ""}
			onClick={handleClick}
		>
			<List.Item>
				<Row>
					<Col span={24}>
						<Space className="!items-start">
							{warningIcon(item)}
							<div className="partial-message">
								{!isClickable ? (
									<span>{item?.notification_text_details}</span>
								) : (
									<Paragraph
										ellipsis={{
											rows: item?.notification_text_details?.length > 20 ? 2 : 1,
										}}
										className="!mb-1 !text-[#1677ff] !underline"
									>
										{item?.notification_text_details}
									</Paragraph>
								)}
							</div>
						</Space>
					</Col>
				</Row>
			</List.Item>
		</div>
	)
}

export const handleNotificationViewed = async (id, updateFn) => {
	const payload = { id: id }
	fetchApi("/notification-set-viewed", "POST", payload)
		.then((res) => {
			if (res?.code === "200") updateFn();
		})
		.catch((err) => { });
};

export const logOut = () => {
	const userType = getCookie(COOKIE.Role)
	let payload = { is_logged_in: 0 };
	if (userType !== "0") {
		payload = { ...payload, is_staff: 1 }
	}
	fetchApi(`/logout/${getCookie(COOKIE.UserId)}`, "POST", payload)
		.then((res) => {
			if (res?.code !== "200") {
				message.error(constants.ErrorMessage);
				return;
			}
			cookiesToDelete();
			const navUrl = userType !== "0" ? "/staff-login" : "/student-login"
			window.location.href = constants.BaseUrl + navUrl;
		})
		.catch(() => {
			message.error(constants.ErrorMessage);
		});
};

export const checkPermission = async () => {
	const userType = getCookie(COOKIE.Role)
	let isStaff = userType === "0" ? "0" : "1"
	let reportingTo = getCookie(COOKIE.ReportingTo) || ''
	let permissionStatus = false
	let result = await fetchApi(`/check-permission?id=${getCookie(COOKIE.UserId)}&is_staff=${isStaff}`, "get")
	if (!result) return false
	if (result?.code === "200") {
		const userData = result?.data
		const apiReportStatus = userData?.reporting_to ? userData?.reporting_to : ''
		const advisorStatus = userData?.is_advisor || '0'
		if (userData.is_active !== "1") {
			logOut()
		} else if (userType !== "0" && (getCookie(COOKIE.Role) !== userData.faculty_roles ||
			reportingTo !== apiReportStatus)
		) {
			logOut()
		} else if (advisorStatus !== getCookie(COOKIE.IsAdvisor)) {
			logOut()
		} else {
			permissionStatus = true
		}
	}
	return permissionStatus
}

export const handleFileUploadChanges = (files, formats, warning) => {
	let hasInvalidFiles = false;
	const validFiles = files.filter((file) => {
		if (!formats?.includes(file?.type) || file?.size / 1024 / 1024 > 2) {
			hasInvalidFiles = true;
			return false;
		}
		return true;
	});

	if (hasInvalidFiles && !warning.current) {
		warning.current = true;
		message.warning(constants.MultipleFileErrorMsg);
		setTimeout(() => warning.current = false, 500);
	}

	return validFiles
}

export const checkNoSemesterActive = (data, navigateFn) => {
	let semValue = 0
	if (data?.length === 1 && Number.parseInt(data?.[0]?.semester_is_ended) === 1) {
		semValue = 1
		showNoSemesterMessageBasedOnRole(navigateFn, true)
	}
	createCookie(COOKIE.NoSemester, semValue, null, '/');
}

export const showNoSemesterMessageBasedOnRole = (navigateFn, showMessage = false) => {
	if (!showMessage) return

	let role = Number.parseInt(getCookie(COOKIE.Role))
	let noSemesterActiveMessage = ''
	if (role === 1 || role === 3) //ADMIN AND SYS ADMIN
		noSemesterActiveMessage = (
			<>
				For the system to function as expected, consider adding the same by clicking{" "}
				<Button
					type="link"
					onClick={() => navigateFn("/create-semester")}
					className="p-0 h-auto text-blue-600 hover:text-blue-800 underline"
				>
					here
				</Button>.
			</>
		);
	else if (role === 0 || role === 2 || role === 4 || role === 5) //STUDENT, FACULTY AND ADJ FACULTY
		noSemesterActiveMessage = "Some functions might not work as expected. For further details, kindly contact the admin."

	if (noSemesterActiveMessage) {
		notification.open({
			message: <b>No semester(s) are currently active.</b>,
			description: <div className='text-slate-500 text-sm'>{noSemesterActiveMessage}</div>,
			duration: 15,
			placement: 'topRight',
			role: 'alert'
		});
	}
}

export const onChangeSubMenu = (keys, subMenuSetter) => {
	const latestOpenKey = keys[keys.length - 1];
	let isMultiSubMenu = keys?.includes('manage-courses')
	const rootSubmenuKeys = [
		'students', 'advising', 'emails',
		'surveys', 'courses', 'manage-courses',
		'gpa', 'staff', 'clinical-settings'
	];
	let keysJ = ''
	if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
		keysJ = isMultiSubMenu ? keys.join() : keys
	} else {
		if (latestOpenKey) {
			keysJ = isMultiSubMenu ? keys.join() : latestOpenKey
		}
	}
	subMenuSetter(keysJ);
	sessionStorage.setItem('selectedSubMenuItem', keysJ)
}

export const onChangeMenu = (item, menuSetter, subMenuSetter) => {
	menuSetter(item.key)
	let hasMoveOutOfCourses = false
	let keyPaths = item?.keyPath
	//CHECK IF KEYS DO NOT CONTAIN COURSES, IN ORDER TO CLOSE COURSES SUB MENUS
	if (keyPaths) {
		hasMoveOutOfCourses = !keyPaths?.includes('courses') && !keyPaths?.includes('manage-courses')
	} else {
		hasMoveOutOfCourses = item.key !== 'manage-courses'
	}
	//SET SELECTED SUB MENUS IN CASE KEYS ARE PRESENT AND SELECTED IS NOT COURSES
	if (hasMoveOutOfCourses && keyPaths) {
		subMenuSetter(keyPaths[keyPaths.length - 1])
		sessionStorage.setItem('selectedSubMenuItem', keyPaths[keyPaths.length - 1])
	}

	sessionStorage.setItem('selectedMenuItem', item.key)
	if (item.keyPath?.length < 2) { //IF SINGLE MENU ITEM IS SELECTED, CLOSE SUBMENU ITEM
		sessionStorage.removeItem('selectedSubMenuItem')
		subMenuSetter('')
	}
}

export const setContextOnLoad = (getter, setter, item) => {
	setter(item)
	sessionStorage.setItem(getter, item)
}

export function getAllSections(sections) {
	return Array.from({ length: sections }, (_, i) => {
		return {
			id: i + 1,
			name: i + 1
		}
	}).concat(constants.extraSections)
}

export const breadcrumbHelper = (routes, setter) => {
	sessionStorage.setItem("breadcrumbItems", JSON.stringify(routes))
	setter(routes)
}

export const handlePhoneKeyPress = (e) => {
	const allowedChars = /[0-9+\-() ]/; // allowed symbols
	if (!allowedChars.test(e.key)) {
		e.preventDefault(); // block invalid keys
	}
};

export function getSectionNo(sectionNumber) {
	let section = Number.parseInt(sectionNumber)
	let sectionNo = section > 8 ?
		constants.extraSections?.find((sec) => sec.id === section)?.name :
		sectionNumber
	return sectionNo
}

export const updateNotificationUrl = (notif, data) => {
	let split = notif?.notification_url?.split('/')
	let index = data?.findIndex((i) => i?.id === split?.[1])
	if (index > -1) {
		notif.notification_url = `${split?.[0]}/${data?.[index]?.id}`
	}
	return notif
}

export function getFailGrades(category, grade, isMaster = true) {
	let filter = (e) => { return constants.GradeDropDown.find((it) => it.label === e)?.value }
	let courses = [
		{ id: 1, category: constants.Academics, fail_grade: 'C+', value: filter('C+') },
		{ id: 2, category: constants.Clinics, fail_grade: 'B-', value: filter('B-') },
		{ id: 3, category: constants.Observations, fail_grade: 'B-', value: filter('B-') },
		{ id: 4, category: constants.Fieldworks, fail_grade: 'B-', value: filter('B-') },
	]
	const course = courses.find(c => c.id === Number.parseInt(category));
	if (!course) return false;
	if (isMaster) return course.fail_grade
	return Number.parseInt(grade) >= Number.parseInt(course.value);
}

export const yearInProgramOption = (type, max = null) => {
	let yearInProgram;
	if(!max) {
		yearInProgram = [...constants.StudentYearList];
	} else {
		yearInProgram = [...constants.StudentYearList].filter(item => item.id <= max);
	}
	type = type ? type?.toString() : '';
	if (type === '2') {
		yearInProgram = yearInProgram.filter((it) => it.type === 2 || it.type === 3)
	} else if (type === '1' || type === '3' || type === '5') {
		yearInProgram = yearInProgram.filter((it) => it.type === 1 || it.type === 2)
	}
	return yearInProgram;
}

export const yearInProgramValue = (type, year, returnValue) => {
	if (!type) return returnValue
	let parsed = Number.parseInt(year)
	if (type === '2' && parsed < 4) return returnValue
	if (type !== '2' && parsed > 5) return returnValue
	return year
}

export const studentTypeValue = (year, type) => {
	let parsedYear = Number.parseInt(year);
	let parsedType = Number.parseInt(type);
	if (!parsedYear) return null
	if (parsedYear > 5) return '2'
	if (parsedYear < 4 && parsedType === 2) return null
	return type
}

export const getYearInProgramObject = (year) => {
	return constants.StudentYearList.find(item => item.id === Number.parseInt(year)) || null;
};

export const getSemesterFieldValue = () => {
	const semesterData = JSON.parse(sessionStorage.getItem('semesterData'));
	return semesterData?.length > 1 ? 2 : 0;
};

export const getSemesterId = (semesterData, selectedRecord, value = null) => {
	const isInstructorCourseSurvey =
		selectedRecord?.survey_type === "2" ||
		selectedRecord?.survey_type === "3";

	let semesterId = null;

	if (semesterData.length > 1) {
		const semesterObj = !isInstructorCourseSurvey
			? semesterData.find(semester => semester?.semester_type === "1")
			: null;

		const selectedValue = value
			? semesterData.some(
				eachSem =>
					eachSem.id === value &&
					eachSem.semester_is_ended === "0"
			)
				? 0
				: 2
			: 0;

		semesterId = semesterObj
			? Number.parseInt(semesterObj.semester_is_ended)
			: selectedValue;
	} else {
		semesterId = 0;
	}

	return semesterId;
};